import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { useAuth } from '../auth/AuthContext';
import { companyMediaCacheKey, getCompanyLogoUrl, getPlatformLogoUrl, resolveCompanyId } from '../../utils/companyIdentity';

const pick = (...values) => values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

/**
 * One logo renderer for auth screens, loaders and the protected workspace.
 * - scope="platform": TELCOFAIR/platform branding first (Login/Register/Reset)
 * - scope="company": current tenant/company branding first (dashboard pages)
 * - explicit src always wins (registration preview, detail cards, etc.)
 *
 * It deliberately keeps a branded TF fallback so the UI never shows a broken/empty logo box.
 */
export default function BrandLogo({
  src = '',
  scope = 'company',
  size = 48,
  radius = 14,
  alt = 'TELCOFAIR',
  sx = {},
  imgSx = {},
  fallbackIcon = false,
}) {
  const { companyData, userData, platformSettings, currentUser } = useAuth();
  const [failedSrc, setFailedSrc] = useState('');
  const companyId = resolveCompanyId({ companyData, userData, currentUser });

  const resolved = useMemo(() => {
    const platform = getPlatformLogoUrl(platformSettings || {});
    const company = getCompanyLogoUrl(companyData || {});
    const cachedPlatform = typeof window !== 'undefined' ? (localStorage.getItem('telcofair_platform_logo') || '') : '';
    const cachedCompany = typeof window !== 'undefined' && companyId
      ? (localStorage.getItem(companyMediaCacheKey(companyId, 'logo')) || '')
      : '';
    const hasLoadedCompany = Boolean(companyData && resolveCompanyId(companyData));

    // Public auth screens are platform-branded only. Protected workspace is
    // company-branded only. Never cross-fallback between those two identities.
    return scope === 'platform'
      ? pick(src, platform, cachedPlatform)
      : pick(src, company, hasLoadedCompany ? '' : cachedCompany);
  }, [src, scope, companyData, userData, platformSettings, companyId]);

  useEffect(() => {
    setFailedSrc('');
    if (typeof window === 'undefined') return;
    if (scope === 'platform') {
      if (resolved) localStorage.setItem('telcofair_platform_logo', resolved);
    } else if (companyId) {
      const key = companyMediaCacheKey(companyId, 'logo');
      if (resolved) localStorage.setItem(key, resolved);
      else if (companyData && resolveCompanyId(companyData)) localStorage.removeItem(key);
    }
  }, [resolved, scope, companyId, companyData]);

  const showImage = Boolean(resolved && failedSrc !== resolved);

  return (
    <Box
      aria-label={`${alt} logo`}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: typeof radius === 'number' ? `${radius}px` : radius,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid rgba(255,255,255,.9)',
        boxShadow: '0 9px 24px rgba(25,57,78,.13), inset 0 0 0 1px rgba(25,57,78,.035)',
        position: 'relative',
        ...sx,
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={resolved}
          alt={`${alt} logo`}
          onError={() => setFailedSrc(resolved)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: '2%', bgcolor: '#fff', ...imgSx }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontWeight: 900,
            fontSize: Math.max(12, Number(size) * 0.29),
            letterSpacing: '-.04em',
            background: 'linear-gradient(145deg,var(--primary,#7A1538),var(--secondary,#B23A5A))',
            position: 'relative',
            '&::after': {
              content: '""', position: 'absolute', inset: '9%', borderRadius: 'inherit',
              border: '1px solid rgba(255,255,255,.28)'
            }
          }}
        >
          {fallbackIcon ? <BusinessRoundedIcon sx={{ fontSize: Number(size) * .46 }} /> : 'TF'}
        </Box>
      )}
    </Box>
  );
}
